/*
  ORTTU native bidirectional endless scroll.

  Rebuilds the winning Intelligems Endless Scroll UX natively by auto-triggering
  the theme's existing Alpine Load More control for downward loading, and adds a
  hidden automatic Load Previous path for clean upward continuity after returning
  to /collections/... ?page=N.

  Expected DOM:
  - #product-grid
  - #pagination-bottom
  - #pagination-bottom button.button--primary with Alpine @click="loadMore"
  - product cards with .card-wrapper[data-product-handle][data-product-page]
*/
(function () {
  if (!/\/collections\//.test(window.location.pathname)) return;
  if (window.__orttuNativeEndlessScroll) return;
  window.__orttuNativeEndlessScroll = true;

  var BUTTON_SELECTOR = '#pagination-bottom button.button--primary';
  var PAGINATION_SELECTOR = '#pagination-bottom';
  var GRID_SELECTOR = '#product-grid';
  var CARD_SELECTOR = '#product-grid .card-wrapper[data-product-handle]';
  var ROOT_MARGIN_PX = 1800;
  var TOP_TRIGGER_PX = 1800;
  var ROOT_MARGIN = ROOT_MARGIN_PX + 'px 0px';
  var MAX_LOCK_MS = 6500;

  var nextLock = false;
  var previousLock = false;
  var observedPagination = null;
  var observer = null;
  var mutationObserver = null;
  var geometryCheckScheduled = false;
  var nextLockTimer = null;
  var previousLockTimer = null;
  var lastScrollY = window.scrollY || window.pageYOffset || 0;
  var lastScrollDirection = null;
  var previousIntent = false;
  var lastTouchY = null;

  function getGrid() {
    return document.querySelector(GRID_SELECTOR);
  }

  function getPagination() {
    return document.querySelector(PAGINATION_SELECTOR);
  }

  function isDisplayNone(el) {
    if (!el) return true;
    try {
      return window.getComputedStyle(el).display === 'none';
    } catch (e) {
      return false;
    }
  }

  function getLoadMoreButton() {
    var button = document.querySelector(BUTTON_SELECTOR);
    if (!button) return null;

    var text = (button.innerText || button.textContent || '').trim().toLowerCase();
    // Avoid accidentally clicking the separate "load previous" button.
    if (text && text.indexOf('load more') === -1) return null;
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return null;

    // Alpine uses display:none when x-show is false. CSS may hide the button
    // visually for endless scroll, but it should remain programmatically clickable.
    if (isDisplayNone(button)) return null;

    return button;
  }

  function productCards() {
    return Array.prototype.slice.call(document.querySelectorAll(CARD_SELECTOR));
  }

  function productCount() {
    return productCards().length;
  }

  function parsePage(value) {
    var page = parseInt(value || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  function currentUrlPage() {
    try {
      return parsePage(new URL(window.location.href).searchParams.get('page') || '1');
    } catch (e) {
      return 1;
    }
  }

  function loadedPages() {
    var pages = {};
    productCards().forEach(function (card) {
      pages[parsePage(card.getAttribute('data-product-page'))] = true;
    });
    return Object.keys(pages).map(function (page) { return parseInt(page, 10); }).sort(function (a, b) { return a - b; });
  }

  function minLoadedPage() {
    var pages = loadedPages();
    return pages.length ? pages[0] : currentUrlPage();
  }

  function hasLoadedPage(page) {
    return loadedPages().indexOf(page) !== -1;
  }

  function pageUrl(page) {
    var url = new URL(window.location.href);
    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', page);
    }
    return url.href;
  }

  function markPageItems(fragment, page) {
    fragment.querySelectorAll('[data-product-handle]').forEach(function (el) {
      if (!el.getAttribute('data-product-page')) {
        el.setAttribute('data-product-page', String(page));
      }
    });
  }

  function dedupeFragment(fragment) {
    fragment.querySelectorAll('[id]').forEach(function (el) {
      if (el.id && document.getElementById(el.id)) {
        var wrapper = el.closest('li') || el;
        if (wrapper && wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
      }
    });
  }

  function fetchGridHtml(page) {
    return fetch(pageUrl(page), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to fetch collection page ' + page);
        return response.text();
      })
      .then(function (html) {
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        var remoteGrid = wrapper.querySelector(GRID_SELECTOR);
        if (!remoteGrid) throw new Error('Missing #product-grid in page ' + page);
        var fragment = document.createElement('div');
        fragment.innerHTML = remoteGrid.innerHTML;
        markPageItems(fragment, page);
        dedupeFragment(fragment);
        return fragment.innerHTML;
      });
  }

  function setReadyState() {
    if (getPagination() && getLoadMoreButton()) {
      document.documentElement.classList.add('orttu-endless-scroll-enabled');
    } else {
      document.documentElement.classList.remove('orttu-endless-scroll-loading');
    }
  }

  function clearNextLockTimer() {
    if (nextLockTimer) {
      window.clearTimeout(nextLockTimer);
      nextLockTimer = null;
    }
  }

  function clearPreviousLockTimer() {
    if (previousLockTimer) {
      window.clearTimeout(previousLockTimer);
      previousLockTimer = null;
    }
  }

  function releaseNextLock() {
    clearNextLockTimer();
    nextLock = false;
    document.documentElement.classList.remove('orttu-endless-scroll-loading');
    attachObserver();
    scheduleGeometryCheck(80);
  }

  function releasePreviousLock() {
    clearPreviousLockTimer();
    previousLock = false;
    previousIntent = false;
    document.documentElement.classList.remove('orttu-endless-scroll-loading-previous');
    attachObserver();
    scheduleGeometryCheck(80);
  }

  function waitForNativeLoadToSettle(clickedButton, startCount, startUrl) {
    var startedAt = Date.now();

    clearNextLockTimer();
    nextLockTimer = window.setTimeout(releaseNextLock, MAX_LOCK_MS);

    function poll() {
      if (!nextLock) return;

      var countChanged = productCount() > startCount;
      var urlChanged = window.location.href !== startUrl;
      var buttonReplaced = clickedButton && !clickedButton.isConnected;
      var noMoreButton = !getLoadMoreButton();
      var timedOut = Date.now() - startedAt > MAX_LOCK_MS;

      if (countChanged || urlChanged || buttonReplaced || noMoreButton || timedOut) {
        releaseNextLock();
        return;
      }

      window.setTimeout(poll, 120);
    }

    poll();
  }

  function triggerLoadMore() {
    if (nextLock || previousLock) return;

    var button = getLoadMoreButton();
    if (!button) {
      setReadyState();
      return;
    }

    var startCount = productCount();
    var startUrl = window.location.href;

    nextLock = true;
    document.documentElement.classList.add('orttu-endless-scroll-enabled');
    document.documentElement.classList.add('orttu-endless-scroll-loading');

    try {
      button.click();
      waitForNativeLoadToSettle(button, startCount, startUrl);
    } catch (e) {
      releaseNextLock();
      if (window.console && console.warn) console.warn('[ORTTU] endless scroll trigger failed', e);
    }
  }

  function canLoadPreviousPage() {
    return !!getGrid() && minLoadedPage() > 1;
  }

  function triggerLoadPrevious() {
    if (previousLock || nextLock || !canLoadPreviousPage()) return;

    var page = minLoadedPage() - 1;
    if (page < 1 || hasLoadedPage(page)) return;

    var grid = getGrid();
    if (!grid) return;

    previousLock = true;
    document.documentElement.classList.add('orttu-endless-scroll-enabled');
    document.documentElement.classList.add('orttu-endless-scroll-loading-previous');

    var beforeHeight = document.documentElement.scrollHeight;
    var beforeScrollY = window.scrollY || window.pageYOffset || 0;
    var boundaryIntent = previousIntent && beforeScrollY <= 80;

    clearPreviousLockTimer();
    previousLockTimer = window.setTimeout(releasePreviousLock, MAX_LOCK_MS);

    fetchGridHtml(page)
      .then(function (html) {
        if (html) {
          grid.insertAdjacentHTML('afterbegin', html);
          var afterHeight = document.documentElement.scrollHeight;
          var delta = afterHeight - beforeHeight;
          if (delta > 0) {
            var targetScrollY = beforeScrollY + delta;
            if (boundaryIntent) {
              // At the exact top of page N, preserving page N's top makes the
              // scrollbar jump downward before the shopper can continue up.
              // Instead, land near the bottom of the newly prepended page N-1,
              // which feels like a continuous upward scroll into previous items.
              targetScrollY = Math.max(0, delta - Math.round(window.innerHeight * 0.85));
            }
            window.scrollTo({ top: targetScrollY, behavior: 'auto' });
          }
        }
      })
      .catch(function (error) {
        if (window.console && console.warn) console.warn('[ORTTU] load previous failed', error);
      })
      .finally(releasePreviousLock);
  }

  function shouldTriggerNextByGeometry() {
    var pagination = getPagination();
    if (!pagination || !getLoadMoreButton()) return false;

    try {
      var rect = pagination.getBoundingClientRect();
      var distanceToBottom = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);

      // IntersectionObserver can miss a very fast scrollbar jump while the
      // pagination block is being replaced. Treat both "sentinel is near/above
      // the viewport" and "shopper is near the document bottom" as signals.
      return rect.top <= window.innerHeight + ROOT_MARGIN_PX || distanceToBottom <= ROOT_MARGIN_PX;
    } catch (e) {
      return false;
    }
  }

  function shouldTriggerPreviousByGeometry() {
    if (!canLoadPreviousPage()) return false;

    var y = window.scrollY || window.pageYOffset || 0;
    var nearTop = y <= TOP_TRIGGER_PX;
    var movingUpNearTop = lastScrollDirection === 'up' && nearTop;

    // If the shopper is already at the top of /?page=N, there is nowhere to
    // scroll upward until we prepend page N-1. A wheel/touch upward intent fills
    // that gap without auto-loading previous pages during initial Back restore.
    return movingUpNearTop || (previousIntent && nearTop);
  }

  function runGeometryCheck() {
    geometryCheckScheduled = false;
    setReadyState();

    if (!previousLock && !nextLock && shouldTriggerPreviousByGeometry()) {
      triggerLoadPrevious();
      return;
    }

    if (!nextLock && !previousLock && shouldTriggerNextByGeometry()) {
      triggerLoadMore();
    }
  }

  function scheduleGeometryCheck(delay) {
    if (geometryCheckScheduled) return;
    geometryCheckScheduled = true;

    var run = function () {
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(runGeometryCheck);
      } else {
        runGeometryCheck();
      }
    };

    if (delay) {
      window.setTimeout(run, delay);
    } else {
      run();
    }
  }

  function attachObserver() {
    var pagination = getPagination();
    if (!pagination) return;

    setReadyState();

    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) triggerLoadMore();
        });
      }, {
        root: null,
        rootMargin: ROOT_MARGIN,
        threshold: 0
      });
    }

    if (observedPagination === pagination) return;
    if (observedPagination) {
      try { observer.unobserve(observedPagination); } catch (e) {}
    }

    observer.observe(pagination);
    observedPagination = pagination;
    scheduleGeometryCheck(0);
  }

  function onScroll() {
    var currentY = window.scrollY || window.pageYOffset || 0;
    if (currentY < lastScrollY - 8) {
      lastScrollDirection = 'up';
    } else if (currentY > lastScrollY + 8) {
      lastScrollDirection = 'down';
      previousIntent = false;
    }
    lastScrollY = currentY;
    scheduleGeometryCheck(0);
  }

  function onWheel(event) {
    if (event && event.deltaY < 0) {
      lastScrollDirection = 'up';
      previousIntent = true;
      scheduleGeometryCheck(0);
    } else if (event && event.deltaY > 0) {
      lastScrollDirection = 'down';
      previousIntent = false;
    }
  }

  function onTouchStart(event) {
    if (event.touches && event.touches.length) {
      lastTouchY = event.touches[0].clientY;
    }
  }

  function onTouchMove(event) {
    if (!event.touches || !event.touches.length || lastTouchY == null) return;
    var y = event.touches[0].clientY;
    if (y > lastTouchY + 8) {
      lastScrollDirection = 'up';
      previousIntent = true;
      scheduleGeometryCheck(0);
    } else if (y < lastTouchY - 8) {
      lastScrollDirection = 'down';
      previousIntent = false;
    }
    lastTouchY = y;
  }

  function init() {
    attachObserver();

    if (!mutationObserver) {
      mutationObserver = new MutationObserver(function () {
        attachObserver();
        scheduleGeometryCheck(0);
      });
      mutationObserver.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('resize', function () { scheduleGeometryCheck(0); }, { passive: true });
    window.addEventListener('pageshow', function () { scheduleGeometryCheck(120); });
    window.addEventListener('load', function () { scheduleGeometryCheck(120); });

    // Alpine/theme scripts can initialize after this deferred asset. Retry a few
    // times so private-window/cold-load timing does not prevent attachment.
    [250, 750, 1500, 3000, 5000].forEach(function (delay) {
      window.setTimeout(function () {
        attachObserver();
        scheduleGeometryCheck(0);
      }, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
